import {
	getDimensionChainAnchors,
	getDimensionGeometry,
	useImageAnnotatorContext,
} from "@msuite/image-annotator";
import type { DimensionAnnotation as DimensionAnnotationType } from "@msuite/utils/types";
import { AnnotationChainHandle } from "./annotation-chain-handle";
import { AnnotationDragHandle } from "./annotation-drag-handle";
import { AnnotationText } from "./annotation-text";
import { formatNumber } from "@msuite/utils/numbers";
import { getSizeUnitAbbreviation } from "@msuite/utils/strings";

/** Props Interface */
interface DimensionAnnotationProps {
	annotation: DimensionAnnotationType;
}

export function DimensionAnnotation({ annotation }: DimensionAnnotationProps) {
	/** Context */
	const context = useImageAnnotatorContext();

	/** Computed */
	const geometry = getDimensionGeometry(annotation, context.baseScale);
	const isSelected = context.controller.selectionId === annotation.id;
	const strokeColor = isSelected ? "var(--color-yellow-500)" : annotation.strokeColor;
	const dashArray = `${5 / context.baseScale} ${3 / context.baseScale}`;

	/** Functions */
	function arrow(tip: { x: number; y: number }, odx: number, ody: number, size: number) {
		const bx = tip.x - odx * size;
		const by = tip.y - ody * size;
		const px = -ody * size * 0.4;
		const py = odx * size * 0.4;
		return `${tip.x},${tip.y} ${bx + px},${by + py} ${bx - px},${by - py}`;
	}

	function handleOnClick(e: React.MouseEvent) {
		if (context.controller.isReadOnly) return;
		/** While a creation tool is armed the click belongs to the canvas, not to selection. */
		if (context.controller.tool !== "select") return;
		e.stopPropagation();
		context.controller.setSelectionId(annotation.id);
	}

	/** Guard */
	if (!geometry) return null;

	/** Render */
	const { ux, uy, s2, e2, mx, my, textAngle, arrowSize, strokeWidth, showMarkers } = geometry;
	const chainAnchors = getDimensionChainAnchors(geometry, context.baseScale * context.scale);

	return (
		<g
			onClick={handleOnClick}
			style={{ cursor: context.controller.isReadOnly ? undefined : "pointer" }}
		>
			{showMarkers && (
				<g>
					<line
						x1={annotation.start.x}
						y1={annotation.start.y}
						x2={s2.x}
						y2={s2.y}
						stroke={strokeColor}
						strokeOpacity={0.5}
						strokeWidth={strokeWidth}
						strokeDasharray={dashArray}
					/>
					<line
						x1={annotation.end.x}
						y1={annotation.end.y}
						x2={e2.x}
						y2={e2.y}
						stroke={strokeColor}
						strokeOpacity={0.5}
						strokeWidth={strokeWidth}
						strokeDasharray={dashArray}
					/>
				</g>
			)}
			<line
				x1={s2.x + ux * arrowSize}
				y1={s2.y + uy * arrowSize}
				x2={e2.x - ux * arrowSize}
				y2={e2.y - uy * arrowSize}
				stroke={strokeColor}
				strokeWidth={strokeWidth}
			/>
			<polygon
				points={arrow(s2, -ux, -uy, arrowSize)}
				fill={strokeColor}
			/>
			<polygon
				points={arrow(e2, ux, uy, arrowSize)}
				fill={strokeColor}
			/>
			{annotation.text && (
				<AnnotationText
					cx={mx}
					cy={my}
					text={[formatNumber(annotation.text, "decimal"), getSizeUnitAbbreviation(annotation.unit)]
						.filter(Boolean)
						.join(" ")}
					transform={`rotate(${textAngle},${mx},${my})`}
				/>
			)}
			{isSelected && !context.controller.isReadOnly && (
				<g>
					<AnnotationDragHandle
						annotationId={annotation.id}
						kind="dim_end"
						cx={annotation.end.x}
						cy={annotation.end.y}
					/>
					<AnnotationDragHandle
						annotationId={annotation.id}
						kind="dim_start"
						cx={annotation.start.x}
						cy={annotation.start.y}
					/>
					<AnnotationChainHandle
						annotation={annotation}
						cx={chainAnchors.start.x}
						cy={chainAnchors.start.y}
						origin={s2}
					/>
					<AnnotationChainHandle
						annotation={annotation}
						cx={chainAnchors.end.x}
						cy={chainAnchors.end.y}
						origin={e2}
					/>
				</g>
			)}
		</g>
	);
}
